import { Component, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../service/auth.service';
import { CommonModule } from '@angular/common';
import { filter, map } from 'rxjs/operators';
import { EonUiButton, EonUiIcon } from '@eon-ui/eon-ui-components-angular';
import { environment } from '../../../environments/environment';
import { CountdownComponent } from './countdown/countdown.component';
import { GameService } from '../../service/game.service';
import { UserService } from '../../service/user.service';
import { CONTENT_PAGES, LOCALE_STORAGE_LANGUAGE } from '../../config/constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog';
import { Route } from '../../enum/route.enum';
import { RouterService } from '../../service/router.service';
import { GameThemeService } from '../../service/game-theme.service';
import { Game } from '../../enum/game.enum';
import { Language } from '../../enum/language.enum';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'gandalf-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [RouterLink, RouterLinkActive, CommonModule, EonUiButton, EonUiIcon, CountdownComponent, TranslatePipe],
  standalone: true,
})
export class HeaderComponent {
  protected Route = Route;
  protected Language = Language;
  protected isContentPage = false;
  protected isDevelopment = !environment.production;
  protected showMobileNav = false;
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private gameService = inject(GameService);
  private gameThemeService = inject(GameThemeService);
  protected readonly assetPath = this.gameThemeService.getImagePath();
  protected readonly eonUiScheme = this.gameThemeService.getEonUiScheme();
  protected readonly eonUiHeaderScheme = this.gameThemeService.getEonUiHeaderScheme();
  protected readonly hasLearnings = this.gameThemeService.hasLearnings();
  protected readonly hasRestart = this.gameThemeService.getCurrentGame() === Game.VISION0;
  protected readonly availableLanguages = this.gameThemeService.getLanguages()();
  private router = inject(Router);
  private routerService = inject(RouterService);
  protected baseUrl = this.routerService.getBaseUrl();
  private translateService = inject(TranslateService);
  protected readonly currentLang = toSignal(this.translateService.onLangChange.pipe(map((e) => e.lang as Language)), {
    initialValue: this.translateService.getCurrentLang() as Language,
  });
  private userService = inject(UserService);
  protected userLevel = this.userService.userLevel();

  constructor() {
    this.updateContentPageStatus(this.router.url);

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects || event.url;
      this.updateContentPageStatus(url);
    });
  }

  /**
   * Checks if user is authenticated using direct token check
   */
  get isAuthenticated(): boolean {
    // Use direct token check instead of computed signal for immediate response
    return this.authService.hasValidAccessToken();
  }

  public closeTab(): void {
    window.close();
  }

  /**
   * Opens a route in a new tab using window.open instead of routerLink with target="_blank"
   * This ensures the new tab shares the same authentication context
   */
  public openInNewTab(route: string): void {
    const url = window.location.origin + '/' + this.routerService.getBaseUrl() + '/' + route;
    window.open(url, '_blank');
  }

  /**
   * Logs out the current user and redirects to home
   */
  public logout(): void {
    console.log('Logging out user...');
    this.authService.logOut();
  }

  public startGame(): void {
    this.gameService.startGame();
  }

  public navigateToGame(): void {
    this.gameService.navigateToGame();
  }

  public confirmRestart(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'dialogs.restart_game.restart_title',
        message: 'dialogs.restart_game.restart_text',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.gameService.restartGame();
      }
    });
  }

  protected switchLang(lang: Language): void {
    localStorage.setItem(LOCALE_STORAGE_LANGUAGE, lang);
    this.translateService.use(lang);
  }

  private updateContentPageStatus(url: string): void {
    this.isContentPage = !!CONTENT_PAGES.find((page) => url.endsWith(`/${page}`));
  }
}
